import { db, emitDbChange, getSetting, pendingMutationCount, tableFor } from "./db";
import { normalizeMedicationKind } from "./health";
import type { MedicationLog, SyncEntityName, SyncTableEntity, TranscriptionJob, VoiceNote } from "./types";

interface SyncChange {
  rev: number;
  entity: SyncEntityName;
  entity_id: string;
  op: "upsert" | "delete";
  payload: SyncTableEntity;
}

interface SyncResponse {
  accepted: Array<{ mutation_id: string; rev: number }>;
  latest_rev: number;
}

interface PullResponse {
  changes: SyncChange[];
  latest_rev: number;
}

export type SyncErrorKind = "auth" | "network" | "server" | "data";

export class SyncError extends Error {
  constructor(
    public readonly kind: SyncErrorKind,
    message: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = "SyncError";
  }
}

export interface SyncResult {
  pushed: number;
  pulled: number;
  pending: number;
  latestRev: number;
  completedAt: string;
}

export interface SyncDiagnostics {
  pending: number;
  latestRev: number;
  lastSyncAt: string | null;
  lastError: string | null;
  lastErrorKind: SyncErrorKind | null;
}

let syncInFlight: Promise<SyncResult> | undefined;

export async function login(password: string): Promise<void> {
  await requestJson<{ authenticated: boolean }>(
    "/api/auth/login",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ password })
    },
    "login"
  );
}

export async function getMe(): Promise<{ authenticated: boolean }> {
  return requestJson("/api/auth/me", { credentials: "include" }, "auth");
}

export async function logout(): Promise<void> {
  await requestJson(
    "/api/auth/logout",
    { method: "POST", credentials: "include" },
    "logout"
  );
}

export async function syncNow(): Promise<SyncResult> {
  if (syncInFlight) {
    return syncInFlight;
  }
  syncInFlight = performSync()
    .catch(async (error: unknown) => {
      const syncError = normalizeSyncError(error);
      await db.settings.bulkPut([
        { key: "last_sync_error", value: syncError.message },
        { key: "last_sync_error_kind", value: syncError.kind }
      ]);
      emitDbChange();
      throw syncError;
    })
    .finally(() => {
      syncInFlight = undefined;
    });
  return syncInFlight;
}

export async function getSyncDiagnostics(): Promise<SyncDiagnostics> {
  const [pending, latestRev, lastSyncAt, lastError, lastErrorKind] = await Promise.all([
    pendingMutationCount(),
    getSetting("last_server_rev", "0"),
    getSetting("last_sync_at"),
    getSetting("last_sync_error"),
    getSetting("last_sync_error_kind")
  ]);
  return {
    pending,
    latestRev: Number.parseInt(latestRev, 10) || 0,
    lastSyncAt: lastSyncAt || null,
    lastError: lastError || null,
    lastErrorKind: isSyncErrorKind(lastErrorKind) ? lastErrorKind : null
  };
}

async function performSync(): Promise<SyncResult> {
  const pushed = await pushMutations();
  const pulled = await pullChanges();
  await uploadVoiceNotes();
  await pollTranscriptionJobs();

  const [pending, latestRevText] = await Promise.all([
    pendingMutationCount(),
    getSetting("last_server_rev", "0")
  ]);
  const completedAt = new Date().toISOString();
  const latestRev = Number.parseInt(latestRevText, 10) || 0;
  await db.settings.bulkPut([
    { key: "last_sync_at", value: completedAt },
    { key: "last_sync_error", value: "" },
    { key: "last_sync_error_kind", value: "" }
  ]);
  emitDbChange();
  return { pushed, pulled, pending, latestRev, completedAt };
}

export async function registerBackgroundSync(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    return;
  }
  const registration = await withTimeout(navigator.serviceWorker.ready, 1_500);
  if (!registration || !("sync" in registration)) {
    return;
  }
  const syncRegistration = (
    registration as ServiceWorkerRegistration & {
      sync: { register: (tag: string) => Promise<void> };
    }
  ).sync.register("tracker-sync");
  await withTimeout(syncRegistration, 1_500);
}

async function pushMutations(): Promise<number> {
  const mutations = await db.sync_mutations
    .filter((mutation) => !mutation.pushed_at)
    .sortBy("created_at");
  if (!mutations.length) {
    return 0;
  }

  const payload = await requestJson<SyncResponse>(
    "/api/sync",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ mutations })
    },
    "push"
  );
  const pushedAt = new Date().toISOString();
  await db.transaction(
    "rw",
    [
      db.sync_mutations,
      db.clients,
      db.client_rates,
      db.projects,
      db.work_sessions,
      db.food_items,
      db.food_logs,
      db.meal_presets,
      db.medication_logs,
      db.symptom_logs,
      db.location_rules,
      db.voice_notes,
      db.transcription_jobs
    ],
    async () => {
      for (const accepted of payload.accepted) {
        const mutation = await db.sync_mutations.get(accepted.mutation_id);
        if (!mutation) {
          continue;
        }
        await db.sync_mutations.update(mutation.id, { pushed_at: pushedAt });
        const remaining = await hasPendingMutation(mutation.entity, mutation.entity_id);
        const table = tableFor(mutation.entity);
        const entity = await table.get(mutation.entity_id);
        if (entity) {
          await table.update(entity.id, {
            server_rev: accepted.rev,
            sync_status: remaining ? "pending" : "synced"
          } as Partial<SyncTableEntity>);
        }
      }
    }
  );
  return payload.accepted.length;
}

async function pullChanges(): Promise<number> {
  const sinceRev = await getSetting("last_server_rev", "0");
  const payload = await requestJson<PullResponse>(
    `/api/sync?since_rev=${encodeURIComponent(sinceRev)}`,
    { credentials: "include" },
    "pull"
  );
  await db.transaction(
    "rw",
    [
      db.sync_mutations,
      db.clients,
      db.client_rates,
      db.projects,
      db.work_sessions,
      db.food_items,
      db.food_logs,
      db.meal_presets,
      db.medication_logs,
      db.symptom_logs,
      db.location_rules,
      db.voice_notes,
      db.transcription_jobs,
      db.settings
    ],
    async () => {
      for (const change of payload.changes) {
        if (await hasPendingMutation(change.entity, change.entity_id)) {
          continue;
        }
        const table = tableFor(change.entity);
        const basePayload =
          change.entity === "medication_logs"
            ? {
                ...change.payload,
                medication: normalizeMedicationKind((change.payload as MedicationLog).medication)
              }
            : change.payload;
        await table.put({
          ...basePayload,
          deleted_at:
            change.op === "delete"
              ? change.payload.deleted_at ?? new Date().toISOString()
              : change.payload.deleted_at,
          server_rev: change.rev,
          sync_status: "synced"
        });
      }
      await db.settings.put({ key: "last_server_rev", value: String(payload.latest_rev) });
    }
  );
  return payload.changes.length;
}

async function hasPendingMutation(entity: SyncEntityName, entityId: string): Promise<boolean> {
  const mutation = await db.sync_mutations
    .where("entity_id")
    .equals(entityId)
    .and((item) => item.entity === entity && !item.pushed_at)
    .first();
  return Boolean(mutation);
}

async function uploadVoiceNotes(): Promise<void> {
  const notes = await db.voice_notes
    .filter((note) => !note.deleted_at && note.upload_status === "pending" && Boolean(note.audio_blob))
    .toArray();

  for (const note of notes) {
    await db.voice_notes.update(note.id, { upload_status: "pending", sync_status: "uploading" });
    const formData = new FormData();
    formData.set("file", note.audio_blob as Blob, `${note.id}.webm`);
    formData.set("mime_type", note.mime_type);
    formData.set("duration_seconds", String(note.duration_seconds));

    try {
      const job = await requestJson<TranscriptionJob>(
        `/api/voice-notes/${note.id}/audio`,
        {
          method: "POST",
          credentials: "include",
          body: formData
        },
        "upload audio"
      );
      await db.transaction("rw", db.voice_notes, db.transcription_jobs, async () => {
        await db.transcription_jobs.put({ ...job, sync_status: "synced" });
        await db.voice_notes.update(note.id, {
          upload_status: "uploaded",
          transcription_status: job.status,
          transcription_job_id: job.id,
          sync_status: "synced"
        });
      });
    } catch (error) {
      await db.voice_notes.update(note.id, { upload_status: "failed", sync_status: "error" });
      throw error;
    }
  }
}

async function pollTranscriptionJobs(): Promise<void> {
  const jobs = await db.transcription_jobs
    .filter((job) => job.status === "queued" || job.status === "running")
    .toArray();

  for (const job of jobs) {
    try {
      const updated = await requestJson<TranscriptionJob>(
        `/api/transcription-jobs/${job.id}`,
        { credentials: "include" },
        "trascrizione"
      );
      await db.transaction("rw", db.transcription_jobs, db.voice_notes, async () => {
        await db.transcription_jobs.put({ ...updated, sync_status: "synced" });
        const patch: Partial<VoiceNote> = {
          transcription_status: updated.status,
          transcript: updated.transcript ?? null
        };
        if (updated.status === "failed") {
          patch.sync_status = "error";
        }
        await db.voice_notes.update(updated.voice_note_id, patch);
      });
    } catch (error) {
      if (error instanceof SyncError && error.kind === "server" && error.status === 404) {
        continue;
      }
      throw error;
    }
  }
}

async function requestJson<T>(
  url: string,
  init: RequestInit,
  operation: string
): Promise<T> {
  let response: Response;
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), 20_000);
  try {
    response = await fetch(url, { ...init, signal: controller.signal });
  } catch {
    throw new SyncError(
      "network",
      "Server non raggiungibile. Verifica il certificato HTTPS o la connessione al Raspberry."
    );
  } finally {
    globalThis.clearTimeout(timer);
  }

  if (!response.ok) {
    if (response.status === 401) {
      throw new SyncError(
        "auth",
        operation === "login" ? "Password non corretta." : "Accesso al backend richiesto.",
        response.status
      );
    }
    if (response.status === 429 && operation === "login") {
      throw new SyncError(
        "auth",
        "Troppi tentativi di accesso. Riprova tra qualche minuto.",
        response.status
      );
    }
    throw new SyncError(
      "server",
      `Errore backend durante ${operation} (${response.status}).`,
      response.status
    );
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new SyncError("data", `Risposta backend non valida durante ${operation}.`);
  }
}

function normalizeSyncError(error: unknown): SyncError {
  if (error instanceof SyncError) {
    return error;
  }
  return new SyncError(
    "data",
    error instanceof Error ? error.message : "Errore locale durante la sincronizzazione."
  );
}

function isSyncErrorKind(value: string): value is SyncErrorKind {
  return value === "auth" || value === "network" || value === "server" || value === "data";
}

async function withTimeout<T>(promise: Promise<T>, milliseconds: number): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timer = globalThis.setTimeout(() => resolve(undefined), milliseconds);
  });
  const result = await Promise.race([promise, timeout]);
  globalThis.clearTimeout(timer);
  return result;
}
